const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

function decodeGeohashRaw(geohash: string): {
  lat: [number, number];
  lon: [number, number];
} {
  let even = true;
  const lat: [number, number] = [-90, 90];
  const lon: [number, number] = [-180, 180];

  for (const char of geohash) {
    const bits = BASE32.indexOf(char);
    for (let i = 4; i >= 0; i--) {
      const bit = (bits >> i) & 1;
      if (even) {
        const mid = (lon[0] + lon[1]) / 2;
        if (bit) lon[0] = mid;
        else lon[1] = mid;
      } else {
        const mid = (lat[0] + lat[1]) / 2;
        if (bit) lat[0] = mid;
        else lat[1] = mid;
      }
      even = !even;
    }
  }

  return { lat, lon };
}

export function encodeGeohash(lat: number, lon: number, precision = 9): string {
  let even = true;
  const latRange: [number, number] = [-90, 90];
  const lonRange: [number, number] = [-180, 180];
  let bit = 0;
  let ch = 0;
  let hash = '';

  while (hash.length < precision) {
    if (even) {
      const mid = (lonRange[0] + lonRange[1]) / 2;
      if (lon >= mid) {
        ch = ch * 2 + 1;
        lonRange[0] = mid;
      } else {
        ch = ch * 2;
        lonRange[1] = mid;
      }
    } else {
      const mid = (latRange[0] + latRange[1]) / 2;
      if (lat >= mid) {
        ch = ch * 2 + 1;
        latRange[0] = mid;
      } else {
        ch = ch * 2;
        latRange[1] = mid;
      }
    }
    even = !even;
    if (bit < 4) {
      bit += 1;
    } else {
      hash += BASE32[ch];
      bit = 0;
      ch = 0;
    }
  }
  return hash;
}

export function decodeGeohash(geohash: string): { lat: number; lon: number } {
  const { lat, lon } = decodeGeohashRaw(geohash);
  return { lat: (lat[0] + lat[1]) / 2, lon: (lon[0] + lon[1]) / 2 };
}

export function decodeGeohashBounds(geohash: string): {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
} {
  const { lat, lon } = decodeGeohashRaw(geohash);
  return { minLat: lat[0], maxLat: lat[1], minLon: lon[0], maxLon: lon[1] };
}

export interface GeoPoint {
  lat: number;
  lon: number;
}

const QUANT = 1e9;

/**
 * Merges a set of same-precision geohash cells into boundary rings.
 * Interior edges shared by two cells cancel out; the remaining edges are
 * chained into closed rings. The first ring returned is the outer boundary
 * (largest area); any further rings are holes.
 */
export function geohashUnionRings(geohashes: string[]): GeoPoint[][] {
  const q = (v: number) => Math.round(v * QUANT);
  const edges = new Map<string, [number, number, number, number]>();

  const toggleEdge = (x1: number, y1: number, x2: number, y2: number) => {
    const forward = x1 < x2 || (x1 === x2 && y1 <= y2);
    const key = forward ? `${x1},${y1},${x2},${y2}` : `${x2},${y2},${x1},${y1}`;
    if (edges.has(key)) edges.delete(key);
    else edges.set(key, [x1, y1, x2, y2]);
  };

  geohashes?.forEach((gh) => {
    let bounds: ReturnType<typeof decodeGeohashBounds>;
    try {
      bounds = decodeGeohashBounds(gh);
    } catch {
      return;
    }
    const x1 = q(bounds.minLon);
    const x2 = q(bounds.maxLon);
    const y1 = q(bounds.minLat);
    const y2 = q(bounds.maxLat);
    toggleEdge(x1, y1, x2, y1);
    toggleEdge(x2, y1, x2, y2);
    toggleEdge(x2, y2, x1, y2);
    toggleEdge(x1, y2, x1, y1);
  });

  // Adjacency: point -> neighboring points along remaining boundary edges
  const adjacency = new Map<string, string[]>();
  const pointKey = (x: number, y: number) => `${x},${y}`;
  edges.forEach(([x1, y1, x2, y2]) => {
    const a = pointKey(x1, y1);
    const b = pointKey(x2, y2);
    adjacency.set(a, [...(adjacency.get(a) ?? []), b]);
    adjacency.set(b, [...(adjacency.get(b) ?? []), a]);
  });

  const removeEdge = (a: string, b: string) => {
    const listA = adjacency.get(a) ?? [];
    const idxA = listA.indexOf(b);
    if (idxA >= 0) listA.splice(idxA, 1);
    const listB = adjacency.get(b) ?? [];
    const idxB = listB.indexOf(a);
    if (idxB >= 0) listB.splice(idxB, 1);
  };

  const rings: GeoPoint[][] = [];
  adjacency.forEach((neighbors, start) => {
    while (neighbors.length > 0) {
      const ring: string[] = [start];
      let next = neighbors[0];
      removeEdge(start, next);
      while (next !== start) {
        ring.push(next);
        const candidates = adjacency.get(next) ?? [];
        if (candidates.length === 0) break;
        const following = candidates[0];
        removeEdge(next, following);
        next = following;
      }
      if (next === start && ring.length >= 4) {
        rings.push(
          ring.map((p) => {
            const [x, y] = p.split(',').map(Number);
            return { lon: x / QUANT, lat: y / QUANT };
          })
        );
      }
    }
  });

  // Outer ring first (largest absolute shoelace area)
  const ringArea = (ring: GeoPoint[]) => {
    let area = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      area += a.lon * b.lat - b.lon * a.lat;
    }
    return Math.abs(area / 2);
  };
  return rings.sort((a, b) => ringArea(b) - ringArea(a));
}

/**
 * Chaikin corner-cutting: rounds a closed ring's 90° grid-cell corners into
 * a smooth curve, for display purposes only (e.g. a geohash-cell boundary
 * that would otherwise render as a staircase).
 */
export function chaikinSmooth(ring: GeoPoint[], iterations = 3): GeoPoint[] {
  let points = ring;
  for (let iter = 0; iter < iterations; iter++) {
    if (points.length < 3) break;
    const next: GeoPoint[] = [];
    for (let i = 0; i < points.length; i++) {
      const p0 = points[i];
      const p1 = points[(i + 1) % points.length];
      next.push(
        {
          lat: 0.75 * p0.lat + 0.25 * p1.lat,
          lon: 0.75 * p0.lon + 0.25 * p1.lon,
        },
        {
          lat: 0.25 * p0.lat + 0.75 * p1.lat,
          lon: 0.25 * p0.lon + 0.75 * p1.lon,
        }
      );
    }
    points = next;
  }
  return points;
}

export function pointInGeoRing(
  lat: number,
  lon: number,
  ring: GeoPoint[]
): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].lon;
    const yi = ring[i].lat;
    const xj = ring[j].lon;
    const yj = ring[j].lat;
    const intersects =
      yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Convex hull (Andrew monotone chain) around the corners of all geohash
 * cells — a single clean boundary polygon covering the whole area.
 */
/** Convex hull (Andrew's monotone chain) over arbitrary lon/lat points. */
export function convexHull(points: GeoPoint[]): GeoPoint[] {
  if (points.length < 3) return [];

  const sorted = [...points].sort((a, b) => a.lon - b.lon || a.lat - b.lat);
  const cross = (o: GeoPoint, a: GeoPoint, b: GeoPoint) =>
    (a.lon - o.lon) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lon - o.lon);

  const lower: GeoPoint[] = [];
  sorted.forEach((p) => {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  });

  const upper: GeoPoint[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }

  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

export function geohashConvexHull(geohashes: string[]): GeoPoint[] {
  const points: GeoPoint[] = [];
  geohashes?.forEach((gh) => {
    try {
      const { minLat, maxLat, minLon, maxLon } = decodeGeohashBounds(gh);
      points.push(
        { lon: minLon, lat: minLat },
        { lon: maxLon, lat: minLat },
        { lon: maxLon, lat: maxLat },
        { lon: minLon, lat: maxLat }
      );
    } catch {
      // skip invalid geohash
    }
  });
  return convexHull(points);
}

export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

/** Drawn boundary vertices -> GeoJSON Polygon (lon,lat order), ring closed */
export function geoPointsToZoneGeog(
  vertices: GeoPoint[]
): GeoJSONPolygon | null {
  if (vertices.length < 3) return null;
  const ring = vertices.map((v) => [v.lon, v.lat]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);
  return { type: 'Polygon', coordinates: [ring] };
}

/** GeoJSON Polygon outer ring -> draft vertices, dropping the closing duplicate */
export function zoneGeogToGeoPoints(
  polygon: GeoJSONPolygon | null | undefined
): GeoPoint[] {
  const ring = polygon?.coordinates?.[0];
  if (!ring || ring.length < 3) return [];
  const points = ring.map(([lon, lat]) => ({ lat, lon }));
  const first = points[0];
  const last = points[points.length - 1];
  if (points.length > 1 && first.lat === last.lat && first.lon === last.lon) {
    points.pop();
  }
  return points;
}

export interface GeoJSONLineString {
  type: 'LineString';
  coordinates: number[][];
}

/** Drawn road path -> GeoJSON LineString (lon,lat order), full precision */
export function geoPointsToPathGeog(
  path: GeoPoint[]
): GeoJSONLineString | null {
  if (path.length < 2) return null;
  return { type: 'LineString', coordinates: path.map((v) => [v.lon, v.lat]) };
}

/** GeoJSON LineString -> road path vertices */
export function pathGeogToGeoPoints(
  line: GeoJSONLineString | null | undefined
): GeoPoint[] {
  const coords = line?.coordinates;
  if (!coords || coords.length < 2) return [];
  return coords.map(([lon, lat]) => ({ lat, lon }));
}
